import { useEffect, useState } from 'react';
import type { SidebarName } from './components/SidebarPanel';
import { useLatest } from 'hooks/useLatest';

// The study list and its toolbar render into separate React roots; use a
// window event for communication rather than adding more Backbone.
const OPEN_SIDEBAR_EVENT = 'studyList:openSidebar';
const SIDEBAR_CHANGE_EVENT = 'studyList:sidebarChanged';

// Hard-coded rather than generated, so triggers in the study list can point
// aria-controls at the sidebar in the different React root.
export const TAGS_SIDEBAR_ID = 'study-list-tags-sidebar';

export type OpenSidebarRequest = {
  name: SidebarName;
  trigger?: HTMLButtonElement;
};

export const requestOpenSidebar = (request: OpenSidebarRequest) => {
  window.dispatchEvent(
    new CustomEvent<OpenSidebarRequest>(OPEN_SIDEBAR_EVENT, {
      detail: request,
    })
  );
};

// Study list items can mount after the toolbar, so store the last opened sidebar here for correct initial state
let openSidebar: SidebarName | null = null;

export const dispatchSidebarChangeEvent = (name: SidebarName | null) => {
  openSidebar = name;
  window.dispatchEvent(
    new CustomEvent<SidebarName | null>(SIDEBAR_CHANGE_EVENT, {
      detail: name,
    })
  );
};

export function useOpenSidebarRequested(
  onRequest: (request: OpenSidebarRequest) => void
) {
  const latestOnRequest = useLatest(onRequest);

  useEffect(() => {
    const listener = (event: Event) =>
      latestOnRequest.current(
        (event as CustomEvent<OpenSidebarRequest>).detail
      );

    window.addEventListener(OPEN_SIDEBAR_EVENT, listener);
    return () => window.removeEventListener(OPEN_SIDEBAR_EVENT, listener);
  }, [latestOnRequest]);
}

export function useSidebarChangeEvent(): SidebarName | null {
  const [currentSidebar, setCurrentSidebar] = useState(openSidebar);

  useEffect(() => {
    const onSidebarChange = (event: Event) =>
      setCurrentSidebar((event as CustomEvent<SidebarName | null>).detail);
    window.addEventListener(SIDEBAR_CHANGE_EVENT, onSidebarChange);

    setCurrentSidebar(openSidebar); // Catch an announcement made between render and this effect

    return () =>
      window.removeEventListener(SIDEBAR_CHANGE_EVENT, onSidebarChange);
  }, []);

  return currentSidebar;
}
